import { useEffect, useLayoutEffect, useRef, useState } from 'react';

interface AnimatedHeightResult {
  contentRef: React.RefObject<HTMLDivElement | null>;
  maxHeight: string;
  height: number;
}

/**
 * Measures the natural height of dynamic content and exposes a maxHeight string
 * suitable for CSS transitions (0px ↔ Npx). Uses ResizeObserver for live updates.
 */
export const useAnimatedHeight = (isOpen: boolean): AnimatedHeightResult => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);

  const measure = () => {
    const el = contentRef.current;
    if (!el) return;
    // scrollHeight is integer-rounded, while fluid typography can produce a
    // fractional border box. Always reserve enough space for both.
    const next = Math.ceil(Math.max(el.scrollHeight, el.getBoundingClientRect().height));
    setHeight(next);
  };

  useLayoutEffect(() => {
    measure();
  }, []);

  useEffect(() => {
    // Re-measure on open/close toggles in case layout shifts
    measure();
  }, [isOpen]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen]);

  const maxHeight = isOpen ? `${height}px` : '0px';

  return { contentRef, maxHeight, height };
};
